import { apiUrl } from "../config/api";
import { userAuthHeaders } from "./authHeaders";

const USER_PORTAL_CACHE_KEY = "userPortalDetailsCache";
const USER_PORTAL_CACHE_TTL_MS = 12 * 60 * 60 * 1000;

function getLoginPath() {
  const publicUrl = process.env.PUBLIC_URL || "";
  const basePath = publicUrl.replace(/\/+$/, "");
  return `${basePath}/loginpage`;
}

export function clearUserSession() {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem(USER_PORTAL_CACHE_KEY);
}

export function redirectToUserLogin() {
  if (window.location.pathname.endsWith("/loginpage")) {
    return;
  }

  window.location.assign(getLoginPath());
}

export function handleUserAuthFailure(response) {
  if (response?.status === 401 || response?.status === 403) {
    clearUserSession();
    redirectToUserLogin();
    return true;
  }

  return false;
}

function createAuthError(message, status = 401) {
  const error = new Error(message);
  error.status = status;
  error.isAuthError = status === 401 || status === 403;
  return error;
}

function findMatchedUser(users, currentUser) {
  if (!Array.isArray(users) || !currentUser) {
    return null;
  }

  const currentId = currentUser.id != null ? String(currentUser.id) : null;
  const currentPhone = currentUser.phone || currentUser.identifier || null;

  return (
    users.find((item) => item?.id != null && currentId && String(item.id) === currentId) ||
    users.find((item) => currentPhone && (item?.phone === currentPhone || item?.identifier === currentPhone)) ||
    null
  );
}

function normalizePortalDetails(payload, currentUser) {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  if (payload.user) {
    return {
      ...payload,
      user: {
        ...currentUser,
        ...payload.user,
      },
      transactions: payload.transactions || [],
      statement: payload.statement || [],
    };
  }

  const matchedUser = findMatchedUser(payload.users, currentUser);

  if (!matchedUser) {
    return null;
  }

  return {
    status: payload.status || "success",
    user: {
      ...currentUser,
      ...matchedUser,
    },
    transactions: payload.transactions || [],
    statement: payload.statement || [],
    fallback: true,
  };
}

function getCachedPortalDetails(currentUser) {
  try {
    const cached = JSON.parse(localStorage.getItem(USER_PORTAL_CACHE_KEY) || "null");
    if (!cached?.savedAt || !cached?.details) {
      return null;
    }

    if (Date.now() - cached.savedAt > USER_PORTAL_CACHE_TTL_MS) {
      return null;
    }

    return {
      ...cached.details,
      user: {
        ...currentUser,
        ...(cached.details.user || {}),
      },
      cached: true,
    };
  } catch (error) {
    localStorage.removeItem(USER_PORTAL_CACHE_KEY);
    return null;
  }
}

function savePortalDetails(details) {
  localStorage.setItem(
    USER_PORTAL_CACHE_KEY,
    JSON.stringify({
      savedAt: Date.now(),
      details,
    })
  );

  if (details?.user) {
    localStorage.setItem("user", JSON.stringify(details.user));
  }
}

async function readJsonResponse(response) {
  try {
    return await response.json();
  } catch (error) {
    return {};
  }
}

export async function fetchUserPortalDetails({ forceRefresh = false } = {}) {
  const token = localStorage.getItem("token");
  const userData = localStorage.getItem("user");

  if (!token || !userData) {
    clearUserSession();
    redirectToUserLogin();
    throw createAuthError("User session is not available.");
  }

  const currentUser = JSON.parse(userData);
  const cachedDetails = getCachedPortalDetails(currentUser);

  if (!forceRefresh && cachedDetails) {
    return cachedDetails;
  }

  const response = await fetch(apiUrl("/api/users/me"), {
    headers: userAuthHeaders(),
  });
  const data = await readJsonResponse(response);

  if (!response.ok) {
    handleUserAuthFailure(response);

    throw createAuthError(
      data.error || data.message || "Unable to fetch user details.",
      response.status
    );
  }

  const normalizedData = normalizePortalDetails(data, currentUser);

  if (normalizedData) {
    savePortalDetails(normalizedData);
    return normalizedData;
  }

  const fallbackData = {
    status: "success",
    user: currentUser,
    transactions: [],
    statement: [],
    fallback: true,
  };

  savePortalDetails(fallbackData);

  return fallbackData;
}
